import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import BigNumber from 'bignumber.js';
import { t } from 'i18next';
import { useEffect, useMemo } from 'react';
import { SubscribeStackParamList, SubscribeStackRoutes } from './type';
import { useOfferDetails } from './useOfferDetails.hook';
import { SubscriptionFormParams } from './useSubscribeAssetForm.hook';
import { useSubscribeForm } from './useSubscribeForm.hook';
import { useSubscriptionPreview } from './useSubscriptionPreview.hook';

interface UseSubscribeEditProps {
  offerId: string;
  onLayout: (event: any) => void;
}

export const useSubscribeEdit = ({ offerId, onLayout }: UseSubscribeEditProps) => {
  const navigation = useNavigation<NativeStackNavigationProp<SubscribeStackParamList>>();
  const offerDetails = useOfferDetails({ offerId });
  const form = useSubscribeForm({
    offer: offerDetails.data?.data
  });

  const preview = useSubscriptionPreview({
    requestParams: form.values as Required<SubscriptionFormParams>,
    account: form.account,
    offerDetails: offerDetails.data?.data
  });

  const onSubmit = () => {
    const offerId = offerDetails.data?.data.id;
    const walletId = form.values.walletId;
    const amount = new BigNumber(form.values.subscriptionAmount || '0').toNumber();

    if (!offerId || !walletId || !amount) return;

    preview.mutate({
      data: {
        offerId,
        walletId,
        amount
      }
    });
  };

  const errorText = useMemo(() => {
    if (!preview.isError) return null;
    return t('earn-invest::glossary.error-unable-to-get-preview');
  }, [preview.isError]);

  useEffect(() => {
    if (preview.error || preview.isError || !preview.isSuccess || !preview.data || !offerDetails.data?.data) return;
    const amount = new BigNumber(form.values.subscriptionAmount || '0').toNumber();
    navigation.navigate(SubscribeStackRoutes.Confirm, {
      offer: offerDetails.data?.data,
      preview: preview.data.data,
      account: form.account,
      walletId: form.values.walletId || '',
      asset: offerDetails.data?.data.asset,
      offerId,
      progressCardData: preview.progressCardData,
      textDetailsCardData: preview.textDetailsCardData,
      subscriptionAmount: new BigNumber(amount).toString(),
      autoRenew: form.values.autoRenew,
      estimatedRewardsAfterMaturity: preview.estimatedRewardsAfterMaturity,
      onLayout
    });
  }, [preview.isSuccess]);

  return {
    form,
    preview,
    offerDetails,
    errorText,
    onSubmit
  };
};
